import React from 'react';
import {
  Form,
  Input,
  FormInstance,
  InputNumber,
  Divider,
  Typography,
} from 'antd';
import LocationPicker from '../LocationPicker';
import type { LocationPayload } from '../../types/location';

const { Text } = Typography;

export const DEFAULT_PROMO_CREDITS = 500;

export type MerchantOnboardingFormContentProps = {
  form: FormInstance;
  location: LocationPayload | null;
  onLocationChange: (payload: LocationPayload) => void;
  onAddressTextChange?: (addr: string) => void;
  /** Khi mở lại cùng lead: ép LocationPicker remount theo dữ liệu mới */
  locationPickerKey?: string | number;
  initialAddress?: string;
  initialLat?: number;
  initialLng?: number;
  /** Chỉ dùng trên flow duyệt lead: field Credits + gợi ý mặc định */
  showPromoCreditsField?: boolean;
  /** Chủ yếu cho modal: trễ mount Leaflet khi container chưa có kích thước. */
  deferMapMs?: number;
  /** Add Merchant: email tự sinh trên server sau khi tạo. */
  showEmailField?: boolean;
};

/**
 * Các trường giống màn hình Add Merchant; có thể thêm bước credit khi duyệt lead.
 */
export function MerchantOnboardingFormContent({
  form,
  location,
  onLocationChange,
  onAddressTextChange,
  locationPickerKey,
  initialAddress = '',
  initialLat,
  initialLng,
  showPromoCreditsField = false,
  deferMapMs = 0,
  showEmailField = true,
}: MerchantOnboardingFormContentProps) {
  return (
    <>
      <Form.Item
        name="name"
        label="Owner Name"
        rules={[{ required: true, message: 'Bắt buộc' }]}
      >
        <Input placeholder="Tên chủ quán" />
      </Form.Item>
      {showEmailField ? (
        <Form.Item
          name="email"
          label="Email"
          rules={[
            {
              required: true,
              message: 'Email là bắt buộc để tạo tài khoản merchant',
            },
            { type: 'email', message: 'Email không hợp lệ' },
          ]}
        >
          <Input
            type="email"
            placeholder="email@example.com"
            autoComplete="off"
          />
        </Form.Item>
      ) : (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Email hệ thống sẽ tự tạo sau khi bấm Create Merchant (dạng{' '}
          <Text code>admin+merchant-{'{id}'}@the-urban-hub.com</Text>).
        </Text>
      )}
      <Form.Item
        name="phone"
        label="Phone"
        rules={[{ required: true, message: 'Số điện thoại là bắt buộc' }]}
      >
        <Input placeholder="Số điện thoại" />
      </Form.Item>
      <Form.Item
        name="password"
        label="Password"
        rules={[
          { required: true, message: 'Bắt buộc' },
          {
            min: 8,
            message:
              'Ít nhất 8 ký tự (merchant đăng nhập app bằng mật khẩu này)',
          },
        ]}
      >
        <Input.Password
          placeholder="Mật khẩu đăng nhập app merchant"
          autoComplete="new-password"
        />
      </Form.Item>
      <Form.Item name="address" label="Owner Address">
        <Input placeholder="Địa chỉ chủ quán" />
      </Form.Item>
      <Form.Item
        name="restaurantName"
        label="Restaurant Name"
        rules={[{ required: true, message: 'Bắt buộc' }]}
      >
        <Input placeholder="Tên quán" />
      </Form.Item>
      <Form.Item
        label="Restaurant Address"
        required
        help={
          location &&
          Number.isFinite(location.lat) &&
          Number.isFinite(location.lng)
            ? `Đã chọn vị trí (Lat: ${location.lat.toFixed(
                4
              )}, Lng: ${location.lng.toFixed(4)}) – lat/lng là nguồn chân lý`
            : 'Tìm kiếm địa chỉ hoặc kéo ghim trên bản đồ để chọn vị trí'
        }
      >
        <LocationPicker
          key={locationPickerKey}
          initialAddress={initialAddress}
          initialLat={initialLat}
          initialLng={initialLng}
          deferMapMs={deferMapMs}
          onChange={payload => {
            onLocationChange(payload);
            form.setFieldsValue({
              restaurantAddress: payload.address,
              latitude: payload.lat,
              longitude: payload.lng,
            });
          }}
          onAddressChange={addr => {
            form.setFieldsValue({ restaurantAddress: addr });
            onAddressTextChange?.(addr);
          }}
        />
      </Form.Item>
      <Form.Item name="restaurantAddress" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="latitude" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="longitude" hidden>
        <Input />
      </Form.Item>

      {showPromoCreditsField && (
        <>
          <Divider orientation="left" style={{ marginTop: 8 }}>
            Credit khuyến mãi
          </Divider>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Sau khi tạo quán, hệ thống sẽ cộng số Điểm (ví quảng cáo) dưới đây
            cho tài khoản merchant mới.
          </Text>
          <Form.Item
            name="credits"
            label="Credits"
            initialValue={DEFAULT_PROMO_CREDITS}
            tooltip="Số lượng credit (Điểm) khuyến mãi cho merchant mới khi kích hoạt từ đăng ký web"
            rules={[
              { required: true, message: 'Nhập số credit' },
              {
                type: 'number',
                min: 0,
                max: 1_000_000_000,
                message: 'Giá trị từ 0 đến 1.000.000.000',
              },
            ]}
          >
            <InputNumber
              min={0}
              max={1_000_000_000}
              step={10}
              style={{ width: '100%' }}
              placeholder="Ví dụ: 500"
            />
          </Form.Item>
        </>
      )}
    </>
  );
}
